<script lang="ts">
  import PanelLeft from '@lucide/svelte/icons/panel-left';
  import RefreshCcw from '@lucide/svelte/icons/refresh-ccw';
  import BookOpenText from '@lucide/svelte/icons/book-open-text';
  import ListTodo from '@lucide/svelte/icons/list-todo';
  import TableOfContents from '@lucide/svelte/icons/table-of-contents';
  import LockOpen from '@lucide/svelte/icons/lock-open';
  import Lock from '@lucide/svelte/icons/lock';
  import FileText from '@lucide/svelte/icons/file-text';
  import ChevronDown from '@lucide/svelte/icons/chevron-down';
  import ArrowLeft from '@lucide/svelte/icons/arrow-left';
  import ArrowUp from '@lucide/svelte/icons/arrow-up';
  import MessageCirclePlus from '@lucide/svelte/icons/message-circle-plus';
  import Search from '@lucide/svelte/icons/search';
  import Plus from '@lucide/svelte/icons/plus';
  import Bell from '@lucide/svelte/icons/bell';
  import Signal from '@lucide/svelte/icons/signal';
  import Wifi from '@lucide/svelte/icons/wifi';
  import BatteryFull from '@lucide/svelte/icons/battery-full';

  let { view = 'courses' }: { view?: string } = $props();

  const headerLabels: Record<string, string> = {
    courses: 'Content',
    live: 'Content',
    batches: 'Batches',
    assessment: 'Submissions',
    community: 'Community'
  };
  const lessons = [
    { kind: 'lesson', title: 'Welcome to the course', chips: ['Notes'], complete: true },
    { kind: 'lesson', title: 'Understanding functions', chips: ['1 video', 'Notes'], complete: true },
    { kind: 'lesson', title: 'Functions & graphs', chips: ['1 video', 'Slides'], complete: false },
    { kind: 'exercise', title: 'Put your knowledge into practice', chips: [], complete: false }
  ];
  const attendance = [
    { name: 'Aarav Mehta', email: 'aarav@example.com', status: 'Present' },
    { name: 'Kavya Rao', email: 'kavya@example.com', status: 'Present' },
    { name: 'Zoya Khan', email: 'zoya@example.com', status: 'Late' },
    { name: 'Arjun Shah', email: 'arjun@example.com', status: 'Choose a status' }
  ];
  const batchTabs = ['Overview', 'People', 'Courses', 'Schedule', 'Attendance', 'Materials', 'Notices'];
  const submissions = [
    {
      initials: 'KR',
      student: 'kavya_rao',
      exercise: 'Functions practice',
      lesson: 'Functions & graphs',
      at: 'Wednesday, November 4, 2026 at 3:12:08 PM'
    },
    {
      initials: 'AM',
      student: 'aarav_m',
      exercise: 'Functions practice',
      lesson: 'Functions & graphs',
      at: 'Wednesday, November 4, 2026 at 2:47:31 PM'
    }
  ];
  const questions = [
    {
      votes: 12,
      title: 'A useful way to remember the power rule',
      author: 'Aarav Mehta',
      time: '2 hours ago',
      comments: 5
    },
    {
      votes: 8,
      title: 'How do we know whether a relation is a function?',
      author: 'Kavya Rao',
      time: '5 hours ago',
      comments: 3
    },
    { votes: 6, title: 'Doubt about domain and range', author: 'Zoya Khan', time: 'yesterday', comments: 2 }
  ];
  const heading = $derived(headerLabels[view] ?? 'Content');
</script>

<div class="mobile-app" data-view={view}>
  <div class="status-bar">
    <span>9:41</span><span class="status-icons"><Signal /><Wifi /><BatteryFull /></span>
  </div>

  {#if view === 'website'}
    <div class="browser-bar"><span class="address"><Lock />northfield.edvello.com</span></div>
    <div class="site">
      <div class="site-nav">
        <strong><span class="site-mark">N</span>Northfield Academy</strong><span class="site-login">Login</span>
      </div>
      <div class="site-hero">
        <h4>Mathematics and science, made clear.</h4>
        <p>Live classes and self-paced courses for Grades 8 to 12.</p>
        <div class="site-actions">
          <span class="site-primary">Start Learning</span><span class="site-outline">Browse</span>
        </div>
        <div class="site-image"></div>
      </div>
      <div class="site-courses">
        <small class="site-eyebrow">Selected courses</small>
        <div class="site-row">
          <div class="site-cover"></div>
          <span class="site-tag">Mathematics</span>
          <strong>Mathematics, made clear</strong>
          <p>Functions, graphs and algebra explained step by step.</p>
          <small>12 lessons · 4 exercises</small>
        </div>
      </div>
    </div>
  {:else}
    <header class="app-header">
      <span class="icon-button"><PanelLeft /></span><span class="divider"></span><strong>{heading}</strong>
      <span class="header-spacer"></span>
      {#if view === 'courses'}<span class="progress-ring" style="--progress: 50"><span>50%</span></span>{:else}<span
          class="icon-button"><Bell /></span
        >{/if}
    </header>

    <div class="app-body">
      {#if view === 'courses'}
        <div class="title-row">
          <h4>Content</h4>
          <span class="icon-button outlined"><RefreshCcw /></span>
        </div>
        <div class="count-cards">
          <div><small>Sections</small><strong>2</strong></div>
          <div><small>Lessons</small><strong>3</strong></div>
          <div><small>Exercises</small><strong>1</strong></div>
        </div>
        <div class="section-card">
          <div class="section-head">
            <TableOfContents /><strong>Getting started</strong><span class="badge">3</span><span class="badge">1</span>
          </div>
          {#each lessons as lesson}<div class="content-row">
              <span class="type-icon {lesson.kind}"
                >{#if lesson.kind === 'lesson'}<BookOpenText />{:else}<ListTodo />{/if}</span
              >
              <div class="row-main">
                <strong>{lesson.title}</strong>
                <div class="chips">
                  {#each lesson.chips as chip}<span class="chip">{chip}</span>{/each}{#if lesson.kind === 'lesson'}<span
                      class="chip"
                      class:complete={lesson.complete}>{lesson.complete ? 'Complete' : 'Incomplete'}</span
                    >{/if}
                </div>
              </div>
              <span class="row-side"><LockOpen /><span class="outline-button small">Continue</span></span>
            </div>{/each}
        </div>
      {:else if view === 'live'}
        <h4 class="lesson-title">Functions & graphs</h4>
        <div class="live-card">
          <span class="live-now"><i></i>LIVE NOW</span>
          <strong class="live-heading">Live Session</strong>
          <small>Started 5 min ago · Google Meet</small>
          <div class="live-actions">
            <span class="primary-button">Join now</span><span class="outline-button">Copy link</span>
          </div>
          <div class="live-link">
            <span>meet.google.com/abc-defg-hij</span><span class="outline-button small">Copy</span>
          </div>
        </div>
        <div class="live-card muted">
          <strong class="live-heading">Live Session</strong>
          <small>Nov 6, 2026, 4:00 PM IST · Zoom</small>
          <div class="live-actions">
            <span class="outline-button">Add to calendar <ChevronDown /></span><span class="outline-button"
              >Copy link</span
            >
          </div>
          <small class="starts-in">Starts in 2d 4h</small>
        </div>
        <div class="notes-block">
          <span><FileText />Lesson notes</span>
          <i></i><i></i><i class="short"></i>
        </div>
      {:else if view === 'batches'}
        <span class="back-link"><ArrowLeft />Back to batches</span>
        <div class="title-row start">
          <h4>Grade 10 · Mathematics</h4>
          <span class="badge solid">Active</span>
        </div>
        <small class="subtitle">Apr 1, 2026 to Mar 31, 2027 | Asia/Kolkata</small>
        <div class="tabs">
          {#each batchTabs as tab}<span class:active={tab === 'Attendance'}>{tab}</span>{/each}
        </div>
        <div class="field-block">
          <small>Class date</small>
          <span class="input">04/11/2026</span>
        </div>
        <div class="marked-row">
          <small>3 of 4 marked</small><span class="outline-button small">Mark all present</span>
        </div>
        <div class="roster">
          {#each attendance as student}<div class="roster-item">
              <strong>{student.name}</strong><small>{student.email}</small>
              <span class="select" class:placeholder={student.status === 'Choose a status'}
                >{student.status}<ChevronDown /></span
              >
            </div>{/each}
        </div>
      {:else if view === 'assessment'}
        <div class="title-row">
          <h4>Submitted Exercises</h4>
          <span class="icon-button outlined"><RefreshCcw /></span>
        </div>
        <div class="board">
          {#each [['Submitted', 2], ['In Progress', 1]] as column, index}<div class="board-column">
              <div class="column-head"><span class="count">{column[1]}</span><strong>{column[0]}</strong></div>
              {#each index === 0 ? submissions : submissions.slice(0, 1) as item}<div class="submission-card">
                  <div class="submission-person"><span class="avatar">{item.initials}</span>{item.student}</div>
                  <strong>{item.exercise}</strong>
                  <small class="lesson-ref">#{item.lesson}</small>
                  {#if index === 0}<small class="hint">Needs manual review</small>{/if}
                  <small>{item.at}</small>
                </div>{/each}
            </div>{/each}
        </div>
      {:else}
        <div class="title-row">
          <div>
            <h4>Community</h4>
            <small class="subtitle">Questions and answers shared across your organization's courses.</small>
          </div>
          <span class="icon-button primary"><Plus /></span>
        </div>
        <div class="filter-row">
          <span class="input search"><Search />Find Question</span><span class="select compact">All<ChevronDown /></span
          >
        </div>
        <div class="threads">
          {#each questions as question}<div class="thread">
              <span class="vote"><ArrowUp /><strong>{question.votes}</strong></span>
              <div class="thread-main">
                <strong>{question.title}</strong>
                <small>{question.author} asked {question.time}</small>
                <div class="thread-meta">
                  <span class="course-tag">#Mathematics, made clear</span><span class="comments"
                    ><MessageCirclePlus />{question.comments}</span
                  >
                </div>
              </div>
            </div>{/each}
        </div>
      {/if}
    </div>
  {/if}
  <div class="home-indicator"><span></span></div>
</div>

<style>
  .mobile-app {
    --p: calc(100cqi / 390);
    --fg: #0a0a0a;
    --muted: #737373;
    --line: #e5e5e5;
    --soft: #f5f5f5;
    --brand: #1447e6;
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    color: var(--fg);
    font-family: Geist, sans-serif;
    font-size: calc(14 * var(--p));
    line-height: 1.4;
    text-align: left;
  }
  .mobile-app * {
    box-sizing: border-box;
  }
  .mobile-app :global(svg) {
    width: calc(16 * var(--p));
    height: calc(16 * var(--p));
    flex-shrink: 0;
  }
  h4,
  p {
    margin: 0;
  }
  small {
    font-size: calc(12 * var(--p));
    color: var(--muted);
  }
  .status-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    height: calc(56 * var(--p));
    padding: calc(8 * var(--p)) calc(34 * var(--p)) 0 calc(44 * var(--p));
    font-size: calc(16 * var(--p));
    font-weight: 600;
  }
  .status-icons {
    display: flex;
    gap: calc(5 * var(--p));
  }
  .app-header {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--p));
    flex-shrink: 0;
    height: calc(48 * var(--p));
    padding: 0 calc(14 * var(--p));
    border-bottom: 1px solid var(--line);
  }
  .app-header strong {
    font-size: calc(14 * var(--p));
    font-weight: 500;
  }
  .divider {
    width: 1px;
    height: calc(16 * var(--p));
    background: var(--line);
  }
  .header-spacer {
    flex: 1;
  }
  .icon-button {
    display: grid;
    place-items: center;
    width: calc(32 * var(--p));
    height: calc(32 * var(--p));
    border-radius: calc(8 * var(--p));
    color: #404040;
  }
  .icon-button.outlined {
    border: 1px solid var(--line);
  }
  .icon-button.primary {
    background: var(--brand);
    color: #fff;
  }
  .progress-ring {
    display: grid;
    place-items: center;
    width: calc(32 * var(--p));
    height: calc(32 * var(--p));
    border-radius: 50%;
    background: conic-gradient(var(--brand) calc(var(--progress) * 1%), var(--line) 0);
  }
  .progress-ring span {
    display: grid;
    place-items: center;
    width: calc(26 * var(--p));
    height: calc(26 * var(--p));
    border-radius: 50%;
    background: #fff;
    font-size: calc(8 * var(--p));
    font-weight: 600;
  }
  .app-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: calc(12 * var(--p));
    padding: calc(16 * var(--p)) calc(18 * var(--p)) 0;
  }
  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(10 * var(--p));
  }
  .title-row.start {
    justify-content: flex-start;
  }
  .title-row h4,
  .lesson-title {
    font-size: calc(22 * var(--p));
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  .subtitle {
    display: block;
    margin-top: calc(-6 * var(--p));
  }
  .title-row .subtitle {
    margin-top: calc(2 * var(--p));
  }
  .badge {
    display: inline-grid;
    place-items: center;
    min-width: calc(20 * var(--p));
    padding: calc(2 * var(--p)) calc(7 * var(--p));
    border-radius: 999px;
    background: #fff;
    border: 1px solid var(--line);
    font-size: calc(11 * var(--p));
    font-weight: 500;
  }
  .badge.solid {
    background: var(--fg);
    border-color: var(--fg);
    color: #fff;
  }
  .count-cards {
    display: grid;
    gap: calc(6 * var(--p));
  }
  .count-cards div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: calc(8 * var(--p)) calc(12 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(10 * var(--p));
  }
  .count-cards strong {
    font-size: calc(18 * var(--p));
    font-weight: 600;
  }
  .section-card {
    overflow: hidden;
    border: 1px solid var(--line);
    border-radius: calc(10 * var(--p));
  }
  .section-head {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--p));
    height: calc(46 * var(--p));
    padding: 0 calc(12 * var(--p));
    background: var(--soft);
  }
  .section-head strong {
    flex: 1;
    font-weight: 500;
  }
  .content-row {
    display: flex;
    align-items: flex-start;
    gap: calc(10 * var(--p));
    padding: calc(11 * var(--p)) calc(12 * var(--p));
    border-top: 1px solid var(--line);
  }
  .type-icon {
    padding-top: calc(2 * var(--p));
  }
  .type-icon.lesson {
    color: #0284c7;
  }
  .type-icon.exercise {
    color: #16a34a;
  }
  .row-main {
    flex: 1;
    min-width: 0;
  }
  .row-main strong {
    display: block;
    overflow: hidden;
    font-size: calc(13 * var(--p));
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: calc(4 * var(--p));
    margin-top: calc(5 * var(--p));
  }
  .chip {
    padding: calc(1 * var(--p)) calc(7 * var(--p));
    border-radius: 999px;
    background: var(--soft);
    font-size: calc(10 * var(--p));
    color: #525252;
  }
  .chip.complete {
    background: #dcfce7;
    color: #15803d;
  }
  .row-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: calc(6 * var(--p));
    color: var(--muted);
  }
  .outline-button,
  .primary-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: calc(6 * var(--p));
    height: calc(36 * var(--p));
    padding: 0 calc(12 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(8 * var(--p));
    background: #fff;
    font-size: calc(13 * var(--p));
    font-weight: 500;
    white-space: nowrap;
    color: var(--fg);
  }
  .outline-button.small {
    height: calc(28 * var(--p));
    padding: 0 calc(9 * var(--p));
    font-size: calc(11 * var(--p));
  }
  .primary-button {
    border-color: var(--brand);
    background: var(--brand);
    color: #fff;
  }
  .live-card {
    display: grid;
    gap: calc(6 * var(--p));
    padding: calc(14 * var(--p));
    border: 1px solid #1447e64d;
    border-radius: calc(12 * var(--p));
  }
  .live-card.muted {
    border-color: var(--line);
  }
  .live-now {
    display: flex;
    align-items: center;
    gap: calc(6 * var(--p));
    color: #dc2626;
    font-size: calc(11 * var(--p));
    font-weight: 600;
    letter-spacing: 0.04em;
  }
  .live-now i {
    width: calc(8 * var(--p));
    height: calc(8 * var(--p));
    border-radius: 50%;
    background: #ef4444;
  }
  .live-heading {
    font-size: calc(16 * var(--p));
    font-weight: 600;
  }
  .live-actions {
    display: flex;
    flex-wrap: wrap;
    gap: calc(8 * var(--p));
    margin-top: calc(4 * var(--p));
  }
  .live-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(8 * var(--p));
    padding: calc(6 * var(--p)) calc(6 * var(--p)) calc(6 * var(--p)) calc(10 * var(--p));
    border-radius: calc(8 * var(--p));
    background: var(--soft);
    font-size: calc(11 * var(--p));
    color: #525252;
  }
  .starts-in {
    font-weight: 500;
  }
  .notes-block {
    display: grid;
    gap: calc(8 * var(--p));
  }
  .notes-block span {
    display: flex;
    align-items: center;
    gap: calc(6 * var(--p));
    font-weight: 500;
  }
  .notes-block i {
    height: calc(8 * var(--p));
    border-radius: 99px;
    background: var(--soft);
  }
  .notes-block i.short {
    width: 60%;
  }
  .back-link {
    display: flex;
    align-items: center;
    gap: calc(6 * var(--p));
    color: var(--muted);
    font-size: calc(12 * var(--p));
  }
  .tabs {
    display: flex;
    flex-wrap: wrap;
    gap: calc(2 * var(--p));
    padding: calc(3 * var(--p));
    border-radius: calc(10 * var(--p));
    background: var(--soft);
  }
  .tabs span {
    padding: calc(5 * var(--p)) calc(9 * var(--p));
    border-radius: calc(7 * var(--p));
    font-size: calc(12 * var(--p));
    color: #525252;
  }
  .tabs .active {
    background: #fff;
    color: var(--fg);
    font-weight: 500;
    box-shadow: 0 1px 2px #0000001a;
  }
  .field-block {
    display: grid;
    gap: calc(4 * var(--p));
  }
  .field-block small {
    color: var(--fg);
    font-weight: 500;
  }
  .input,
  .select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(8 * var(--p));
    height: calc(36 * var(--p));
    padding: 0 calc(10 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(8 * var(--p));
    font-size: calc(13 * var(--p));
  }
  .select.placeholder {
    color: var(--muted);
  }
  .select :global(svg) {
    color: var(--muted);
  }
  .marked-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .roster {
    display: grid;
    gap: calc(8 * var(--p));
  }
  .roster-item {
    display: grid;
    gap: calc(2 * var(--p));
    padding: calc(10 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(10 * var(--p));
  }
  .roster-item strong {
    font-size: calc(13 * var(--p));
    font-weight: 500;
  }
  .roster-item .select {
    margin-top: calc(6 * var(--p));
  }
  .board {
    display: flex;
    gap: calc(12 * var(--p));
    margin-right: calc(-18 * var(--p));
  }
  .board-column {
    flex: 0 0 calc(270 * var(--p));
    display: grid;
    align-content: start;
    gap: calc(8 * var(--p));
    padding: calc(10 * var(--p));
    border-radius: calc(12 * var(--p));
    background: var(--soft);
  }
  .column-head {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--p));
    font-size: calc(13 * var(--p));
  }
  .column-head strong {
    font-weight: 500;
  }
  .count {
    display: grid;
    place-items: center;
    min-width: calc(22 * var(--p));
    height: calc(22 * var(--p));
    border-radius: 999px;
    background: #fff;
    font-size: calc(11 * var(--p));
    font-weight: 600;
  }
  .submission-card {
    display: grid;
    gap: calc(4 * var(--p));
    padding: calc(12 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(10 * var(--p));
    background: #fff;
  }
  .submission-person {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--p));
    font-size: calc(12 * var(--p));
    color: #404040;
  }
  .submission-card > strong {
    color: var(--brand);
    font-size: calc(13 * var(--p));
    font-weight: 500;
  }
  .lesson-ref {
    color: #404040;
  }
  .hint {
    color: #b45309;
  }
  .avatar {
    display: grid;
    place-items: center;
    width: calc(26 * var(--p));
    height: calc(26 * var(--p));
    border-radius: 50%;
    background: #e5e7eb;
    color: #404040;
    font-size: calc(10 * var(--p));
    font-weight: 600;
  }
  .filter-row {
    display: flex;
    gap: calc(8 * var(--p));
  }
  .input.search {
    flex: 1;
    justify-content: flex-start;
    color: var(--muted);
  }
  .select.compact {
    width: calc(76 * var(--p));
  }
  .threads {
    display: grid;
    gap: calc(8 * var(--p));
  }
  .thread {
    display: flex;
    gap: calc(12 * var(--p));
    padding: calc(12 * var(--p));
    border: 1px solid var(--line);
    border-radius: calc(10 * var(--p));
  }
  .vote {
    display: grid;
    justify-items: center;
    align-self: start;
    gap: calc(2 * var(--p));
    color: #404040;
    font-size: calc(12 * var(--p));
  }
  .thread-main {
    display: grid;
    gap: calc(3 * var(--p));
    min-width: 0;
  }
  .thread-main > strong {
    font-size: calc(13 * var(--p));
    font-weight: 500;
  }
  .thread-meta {
    display: flex;
    align-items: center;
    gap: calc(10 * var(--p));
    font-size: calc(11 * var(--p));
  }
  .course-tag {
    color: var(--brand);
  }
  .comments {
    display: inline-flex;
    align-items: center;
    gap: calc(4 * var(--p));
    color: var(--muted);
  }
  .comments :global(svg) {
    width: calc(13 * var(--p));
    height: calc(13 * var(--p));
  }
  .home-indicator {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    height: calc(28 * var(--p));
    background: #fff;
  }
  .home-indicator span {
    width: calc(134 * var(--p));
    height: calc(5 * var(--p));
    border-radius: 999px;
    background: #111;
  }
  .browser-bar {
    padding: calc(2 * var(--p)) calc(14 * var(--p)) calc(10 * var(--p));
  }
  .address {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: calc(6 * var(--p));
    padding: calc(9 * var(--p));
    border-radius: calc(12 * var(--p));
    background: #eceef2;
    color: #404040;
    font-size: calc(13 * var(--p));
  }
  .address :global(svg) {
    width: calc(12 * var(--p));
    height: calc(12 * var(--p));
  }
  .site {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #f3f3f4;
    color: #111114;
  }
  .site-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: calc(58 * var(--p));
    padding: 0 calc(16 * var(--p));
    border-bottom: 1px solid #e2e2e5;
    background: #fff;
  }
  .site-nav strong {
    display: flex;
    align-items: center;
    gap: calc(8 * var(--p));
    font-size: calc(14 * var(--p));
    font-weight: 600;
  }
  .site-mark {
    display: grid;
    place-items: center;
    width: calc(26 * var(--p));
    height: calc(26 * var(--p));
    background: #111114;
    color: #fff;
    font-size: calc(12 * var(--p));
  }
  .site-login,
  .site-primary {
    padding: calc(8 * var(--p)) calc(14 * var(--p));
    background: var(--brand);
    color: #fff;
    font-size: calc(12 * var(--p));
    font-weight: 500;
  }
  .site-hero {
    padding: calc(24 * var(--p)) calc(18 * var(--p)) calc(18 * var(--p));
  }
  .site-hero h4 {
    font-size: calc(28 * var(--p));
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.03em;
  }
  .site-hero p {
    margin-top: calc(10 * var(--p));
    color: #55555c;
    font-size: calc(13 * var(--p));
  }
  .site-actions {
    display: flex;
    gap: calc(8 * var(--p));
    margin-top: calc(16 * var(--p));
  }
  .site-outline {
    padding: calc(8 * var(--p)) calc(14 * var(--p));
    border: 1px solid #d4d4d8;
    background: #fff;
    font-size: calc(12 * var(--p));
    font-weight: 500;
  }
  .site-image,
  .site-cover {
    aspect-ratio: 4 / 3;
    margin-top: calc(18 * var(--p));
    background: linear-gradient(135deg, #dbe4ff, #c7d4f7 55%, #e9e3c4);
  }
  .site-courses {
    padding: 0 calc(18 * var(--p));
  }
  .site-eyebrow {
    display: block;
    margin-bottom: calc(10 * var(--p));
    font-weight: 500;
    color: #55555c;
  }
  .site-row {
    display: grid;
    gap: calc(4 * var(--p));
    padding: calc(12 * var(--p));
    border: 1px solid #e2e2e5;
    background: #fff;
  }
  .site-row .site-cover {
    margin-top: 0;
    margin-bottom: calc(8 * var(--p));
  }
  .site-tag {
    justify-self: start;
    padding: calc(2 * var(--p)) calc(8 * var(--p));
    border: 1px solid #e2e2e5;
    font-size: calc(10 * var(--p));
  }
  .site-row strong {
    font-size: calc(15 * var(--p));
    font-weight: 600;
  }
  .site-row p {
    color: #55555c;
    font-size: calc(12 * var(--p));
  }
</style>
